import type { Environment, EventType, Severity } from './types'

const createEventType = (type: EventType): EventType => type
const createEnvironment = (env: Environment): Environment => env
const createSeverity = (sev: Severity): Severity => sev

export const INITIATOR_USER = 'user'
export const INITIATOR_SYSTEM = 'system'

export const EventTypes = {
    Auth: {
        LOGIN: createEventType('USER_LOGIN'),
        LOGOUT: createEventType('USER_LOGOUT'),
        REGISTER: createEventType('USER_REGISTER'),
    },
    Action: {
        CLICK: createEventType('ACTION_CLICK'),
        PAGE_VIEW: createEventType('PAGE_VIEW'),
        ANNOUNCEMENT_SEEN: createEventType('ANNOUNCEMENT_SEEN'),
        CODE_REDEEMED: createEventType('CODE_REDEEMED'),
    },
    Class: {
        CREATE: createEventType('CLASS_CREATE'),
        EDIT: createEventType('CLASS_EDIT'),
        DELETE: createEventType('CLASS_DELETE'),
    },
    Material: {
        NEW: createEventType('MATERIAL_NEW'),
        STEP_1: createEventType('MATERIAL_STEP_1'),
        STEP_2: createEventType('MATERIAL_STEP_2'),
        CREATE: createEventType('MATERIAL_CREATE'),
        UPDATE: createEventType('MATERIAL_UPDATE'),
        DELETE: createEventType('MATERIAL_DELETE'),
        CREATE_DOWNLOADS: createEventType('MATERIAL_CREATE_DOWNLOADS'),
        DOWNLOAD: {
            PDF: createEventType('MATERIAL_DOWNLOAD_PDF'),
            DOCX: createEventType('MATERIAL_DOWNLOAD_DOCX'),
        },
    },
    File: {
        UPLOAD_ATTEMPT: createEventType('FILE_UPLOAD_ATTEMPT'),
        UPLOADED: createEventType('FILE_UPLOADED'),
        UPDATE: createEventType('FILE_UPDATE'),
        DELETE: createEventType('FILE_DELETE'),
    },
    Conversation: {
        NEW: createEventType('CONVERSATION_NEW'),
        CREATE: createEventType('CONVERSATION_CREATE'),
        RUN: createEventType('CONVERSATION_RUN'),
        MESSAGE_SENT: createEventType('CONVERSATION_MESSAGE_SENT'),
        ANSWER_GENERATED: createEventType('CONVERSATION_ANSWER_GENERATED'),
        STREAMING_STARTED: createEventType('STREAMING_STARTED'),
        FILE_UPLOAD_SENT: createEventType('CONVERSATION_FILE_UPLOAD_SENT'),
        DELETE: createEventType('CONVERSATION_DELETE'),
        CREATE_DOWNLOADS: createEventType('CONVERSATION_CREATE_DOWNLOADS'),
        DOWNLOAD: {
            PDF: createEventType('CONVERSATION_DOWNLOAD_PDF'),
            DOCX: createEventType('CONVERSATION_DOWNLOAD_DOCX'),
        },
    },
    Expert: {
        RESULT_GENERATED: createEventType('EXPERT_RESULT_GENERATED'),
        TRIGGER: createEventType('EXPERT_TRIGGERED_ANOTHER_ACTION'),
        START: createEventType('EXPERT_START'),
        RUN: createEventType('EXPERT_RUN'),
        FINISH: createEventType('EXPERT_FINISH'),
        LLM_CALL_SENT: createEventType('LLM_CALL_SENT'),
        CALL: createEventType('CALL'),
        CALL_FINISH: createEventType('CALL_FINISH'),
        NODE: createEventType('EXPERT_NODE'),
        API_CALL: createEventType('API_CALL'),
        CALL_ERROR: createEventType('LLM_CALL_ERROR'),
        ERROR: createEventType('ERROR'),
    },
    Picture: {
        ERROR: createEventType('PICTURE_ERROR'),
        POLLING_START: createEventType('POLLING_START'),
        POLLING_END: createEventType('POLLING_END'),
        INITIAL_CREATE: createEventType('PICTURE_INITIAL_CREATE'),
        CUSTOM_IMAGE_VIEW: createEventType('CUSTOM_IMAGE_VIEW'),
        TRIGGER_CREATION: createEventType('TRIGGER_PICTURE_CREATE'),
        DOWNLOAD: createEventType('PICTURE_DOWNLOAD'),
        DOWNLOADED: createEventType('PICTURE_DOWNLOADED'),
        DELETE: createEventType('PICTURE_DELETE'),
        NEW: createEventType('PICTURE_NEW'),
        SHOW_GALLERY_VIEW: createEventType('SHOW_GALLERY_VIEW'),
        SHOW_PICTURE_VIEW: createEventType('SHOW_PICTURE_VIEW'),
        USE_PARAMETER: createEventType('PICTURE_USE_PARAMETER'),
    },
    Gallery: {
        VIEW_IMAGE: createEventType('GALLERY_IMAGE_VIEW'),
        USE_PARAMETER: createEventType('GALLERY_USE_PARAMETER'),
    },
    Microphone: {
        PERMISSION_DENIED: createEventType('PERMISSION_DENIED'),
        START_RECORDING: createEventType('START_RECORDING'),
        STOP_RECORDING: createEventType('STOP_RECORDING'),
        RECOGNIZER_ERROR: createEventType('RECOGNIZER_ERROR'),
    },
    PiiMasking: {
        MASKING_WITH_LLM: createEventType('MASKING_WITH_LLM'),
    },
    User: {
        SET_SOURCE: createEventType('USER_SET_SOURCE'),
        GET_DB_SUBSCRIPTION: createEventType('USER_GET_DB_SUBSCRIPTION'),
    },
    System: {
        ERROR: createEventType('SYSTEM_ERROR'),
    },
    Admin: {
        PROFILE_RESET: createEventType('ADMIN_PROFILE_RESET'),
    },
    DocumentUpload: {
        UPLOAD_STARTED: createEventType('DOCUMENT_UPLOAD_STARTED'),
        ANALYSIS_COMPLETED: createEventType('DOCUMENT_ANALYSIS_COMPLETED'),
        ANALYSIS_FAILED: createEventType('DOCUMENT_ANALYSIS_FAILED'),
    },
} as const

export const LogEnvironments = {
    DEV: createEnvironment('DEV'),
    STAGE: createEnvironment('STAGE'),
    PROD: createEnvironment('PRODUCTION'),
} as const

export const Severities = {
    INFO: createSeverity('INFO'),
    WARNING: createSeverity('WARNING'),
    ERROR: createSeverity('ERROR'),
    DEBUG: createSeverity('DEBUG'),
} as const
